import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search } from "lucide-react";
import DropdownChevron from "./dropdown-chevron";

interface Option {
  id?: number | string;
  name?: string;
  value?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface StyledSelectDropdownProps {
  options: Option[];
  value: string | number | "";
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
  className?: string;
  getOptionLabel?: (option: Option) => string;
  getOptionValue?: (option: Option) => string | number;
  /** Show search field and type-to-filter when the list is open. Default true. */
  searchable?: boolean;
}

export default function StyledSelectDropdown({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  disabledPlaceholder,
  className = "",
  getOptionLabel = (option) => option.name || String(option.id || ""),
  getOptionValue = (option) => option.id ?? option.value ?? "",
  searchable = true,
}: StyledSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (!isOpen || !searchable) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen || disabled || !searchable) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDropdown();
        return;
      }

      const target = event.target as HTMLElement;
      if (target === searchInputRef.current) return;
      if (
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
        !dropdownRef.current?.contains(target)
      ) {
        return;
      }

      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        searchInputRef.current?.focus();
        setSearchQuery((prev) => prev + event.key);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, disabled, searchable, closeDropdown]);

  const selectedOption = options.find(
    (opt) => String(getOptionValue(opt)) === String(value)
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      getOptionLabel(opt).toLowerCase().includes(q)
    );
  }, [options, searchQuery, searchable, getOptionLabel]);

  const displayText = selectedOption
    ? getOptionLabel(selectedOption)
    : disabled && disabledPlaceholder
      ? disabledPlaceholder
      : placeholder;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          if (isOpen) closeDropdown();
          else setIsOpen(true);
        }}
        disabled={disabled}
        className={`w-full bg-[#F9F9F9] px-4 py-3 rounded-md text-small flex items-center justify-between
                   focus:outline-none focus:ring-2 focus:ring-lime-400
                   transition-colors
                   ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`truncate ${selectedOption ? "text-gray-800 font-medium" : "text-gray-400"}`}
          >
            {displayText}
          </span>
        </div>
        <DropdownChevron isOpen={isOpen} disabled={disabled} />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-2 w-full">
          <div className="bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden">
            {searchable && (
              <div
                className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && filteredOptions.length > 0) {
                        e.preventDefault();
                        const first = filteredOptions[0];
                        onChange(String(getOptionValue(first)));
                        closeDropdown();
                      }
                    }}
                    placeholder="Type to search..."
                    className="w-full rounded-md border border-gray-200 bg-[#F9F9F9] py-2 pl-9 pr-3 text-small text-gray-800 placeholder:text-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                    aria-label="Search options"
                  />
                </div>
              </div>
            )}

            <ul className="max-h-64 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <li className="px-4 py-3 text-small text-gray-500">No results found</li>
              ) : (
                filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option);
                  const optionLabel = getOptionLabel(option);
                  const isSelected = String(optionValue) === String(value);
                  const optionKey = `${String(optionValue)}-${index}`;

                  return (
                    <li
                      key={optionKey}
                      onClick={() => {
                        onChange(String(optionValue));
                        closeDropdown();
                      }}
                      className={`px-4 py-3 cursor-pointer transition-colors
                           hover:bg-[#ABD405]
                           flex items-center justify-between
                           ${index !== 0 ? "border-t border-slate-100" : ""}
                           ${isSelected ? "bg-[#ABD405]" : ""}`}
                    >
                      <span className="text-small text-slate-700 font-medium">{optionLabel}</span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
